/**
 * Authentication state store
 * Manages user session, loading state, and auth actions
 */

import { create } from 'zustand';
import type { User, Quota } from '../services/auth.api';
import * as authApi from '../services/auth.api';

interface AuthState {
  user: User | null;
  quota: Quota | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string, profession?: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  quota: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authApi.login({ email, password });
      set({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });

      // Fetch full user data including quota
      await get().fetchMe();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      set({
        isLoading: false,
        error: message,
      });
      throw error;
    }
  },

  register: async (email: string, password: string, displayName?: string, profession?: string) => {
    set({ isLoading: true, error: null });

    try {
      const input: authApi.RegisterInput = {
        email,
        password,
        displayName,
        profession: profession as authApi.Profession | undefined,
      };

      const response = await authApi.register(input);
      set({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });

      // Fetch full user data including quota
      await get().fetchMe();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      set({
        isLoading: false,
        error: message,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });

    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors - we're clearing state anyway
    } finally {
      set({
        ...initialState,
        isLoading: false,
      });
    }
  },

  fetchMe: async () => {
    set({ isLoading: true });

    try {
      const response = await authApi.getMe();
      set({
        user: response.user,
        quota: response.quota,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } catch {
      // Not authenticated or error - reset to unauthenticated state
      set({
        ...initialState,
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}));

// Convenience object for direct access outside React components
export const auth = {
  login: (email: string, password: string) => useAuthStore.getState().login(email, password),
  register: (email: string, password: string, displayName?: string, profession?: string) =>
    useAuthStore.getState().register(email, password, displayName, profession),
  logout: () => useAuthStore.getState().logout(),
  fetchMe: () => useAuthStore.getState().fetchMe(),
  getUser: () => useAuthStore.getState().user,
  isAuthenticated: () => useAuthStore.getState().isAuthenticated,
};
